import type React from 'react'
import { useCallback } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'

import { mergeFreshShapes } from '../helpers/fresh-shapes'
import { createHistoryTxnId } from '../helpers/history-txn-id'
import { parseRotationText, rotationForShapeState } from '../helpers/rotation-degrees'
import { isPanelShapeGeometryBlocked, shapesByIdFromManager } from '../helpers/selection-protection'
import type { ObjectPanelProps } from '../types/object-panel-props'
import type { PanelFieldErrors } from '../types/panel-fields'
import type { PersistShapesFromManager } from '../types/shape-commit'
import type { SingleRotateBefore } from '../types/single-rotate-before'

/** Applying a rotation live (slider ticks) and committing it (presets, slider release, text field). */
export function useRotationCommit(params: {
  activeObject: ShapeData | null
  activeObjects: ShapeData[]
  isMultiSelect: boolean
  shapeManagerRef?: React.RefObject<ShapeManagerRef | null>
  setActiveObject: (obj: ShapeData | null) => void
  setActiveObjects: (objs: ShapeData[]) => void
  onBatchShapesCommitted: ObjectPanelProps['onBatchShapesCommitted']
  persistShapesFromManager: PersistShapesFromManager
  setFieldErrors: React.Dispatch<React.SetStateAction<PanelFieldErrors>>
  rotationRef: React.MutableRefObject<number[]>
  panelSingleRotateBeforeRef: React.MutableRefObject<SingleRotateBefore | null>
  rotationEditStartValueRef: React.MutableRefObject<number | null>
  getMixedMultiSelectPanelRotationDeg: () => number
  syncRotationDisplay: (deg: number) => void
  setRotationDisplay: (deg: number) => void
}) {
  const {
    activeObject,
    activeObjects,
    isMultiSelect,
    shapeManagerRef,
    setActiveObject,
    setActiveObjects,
    onBatchShapesCommitted,
    persistShapesFromManager,
    setFieldErrors,
    rotationRef,
    panelSingleRotateBeforeRef,
    rotationEditStartValueRef,
    getMixedMultiSelectPanelRotationDeg,
    syncRotationDisplay,
    setRotationDisplay,
  } = params

  /** Live canvas + active object (same idea as in-transit resize); safe to call on every slider tick. */
  const applyRotationToSelection = useCallback(
    (degrees: number): number | null => {
      const mgr = shapeManagerRef?.current
      const finalDeg = rotationForShapeState(degrees)

      if (isMultiSelect) {
        if (activeObjects.length >= 2 && mgr) {
          const shapeApplied = mgr.applyPanelMultiSelectRotationPreview(degrees)

          if (shapeApplied === null) {
            return null
          }

          setActiveObjects(mergeFreshShapes(activeObjects, mgr.getShapes()))

          return shapeApplied
        }

        return null
      }

      if (activeObject && mgr) {
        const shapesById = shapesByIdFromManager(mgr)
        const latest = shapesById.get(activeObject.id) ?? activeObject

        if (isPanelShapeGeometryBlocked(latest, shapesById)) {
          return null
        }

        mgr.updateShape(activeObject.id, { rotation: finalDeg })
        setActiveObject({ ...activeObject, rotation: finalDeg })
        return finalDeg
      }

      return null
    },
    [activeObject, activeObjects, isMultiSelect, setActiveObject, setActiveObjects, shapeManagerRef]
  )

  const commitRotation = useCallback(
    (degrees: number) => {
      const mgr = shapeManagerRef?.current

      if (isMultiSelect) {
        if (activeObjects.length === 0) return

        const historyTxnId = createHistoryTxnId('mixed-rotate-panel')

        if (mgr && onBatchShapesCommitted) {
          let items = mgr.finishPanelMultiSelectRotation()

          if (items === null) {
            mgr.beginPanelMultiSelectRotation()

            if (mgr.applyPanelMultiSelectRotationPreview(degrees) === null) {
              return
            }

            items = mgr.finishPanelMultiSelectRotation()
          }

          if (!items) {
            return
          }

          if (items.length > 0) {
            onBatchShapesCommitted(items, 'rotate', { historyTxnId })
          }
        }

        const finalDeg = rotationForShapeState(degrees)

        setRotationDisplay(finalDeg)
        setFieldErrors((prev) => ({ ...prev, rotation: undefined }))

        if (mgr) {
          setActiveObjects(mergeFreshShapes(activeObjects, mgr.getShapes()))
        }

        return
      }

      if (!activeObject?.id) return
      const beforeShapeRot =
        panelSingleRotateBeforeRef.current?.kind === 'shape' &&
        panelSingleRotateBeforeRef.current.id === activeObject.id
          ? panelSingleRotateBeforeRef.current.rotation
          : rotationForShapeState(activeObject.rotation ?? 0)
      const finalDeg = applyRotationToSelection(degrees)

      if (finalDeg === null) return

      setRotationDisplay(finalDeg)
      setFieldErrors((prev) => ({ ...prev, rotation: undefined }))
      persistShapesFromManager([activeObject.id], 'rotate', {
        before: { rotation: beforeShapeRot },
        after: { rotation: finalDeg },
      })

      panelSingleRotateBeforeRef.current = null
    },
    [
      activeObject,
      activeObjects,
      applyRotationToSelection,
      isMultiSelect,
      onBatchShapesCommitted,
      persistShapesFromManager,
      shapeManagerRef,
      setActiveObjects,
      setRotationDisplay,
      setFieldErrors,
      panelSingleRotateBeforeRef,
    ]
  )

  const commitRotationFromText = useCallback(
    (raw: string) => {
      const mgr = shapeManagerRef?.current
      const editStartRot = rotationEditStartValueRef.current

      rotationEditStartValueRef.current = null
      mgr?.updatePanelRotationGuidePreview(null)

      // The selection's current rotation — read only when reverting an invalid entry.
      let readCurrentRotation: () => number

      if (isMultiSelect) {
        if (activeObjects.length === 0) return
        readCurrentRotation = getMixedMultiSelectPanelRotationDeg
      } else {
        if (!activeObject) return
        const currentRot = rotationForShapeState(activeObject.rotation ?? 0)

        readCurrentRotation = () => currentRot
      }

      const result = parseRotationText(raw)

      if ('error' in result) {
        setFieldErrors((prev) => ({ ...prev, rotation: result.error }))
        const currentRot = readCurrentRotation()
        const revertRot = editStartRot ?? currentRot

        setRotationDisplay(revertRot)
        if (editStartRot != null && editStartRot !== currentRot) {
          commitRotation(revertRot)
        }

        return
      }

      const { deg } = result

      if (deg === rotationForShapeState(rotationRef.current[0] ?? 0)) {
        setFieldErrors((prev) => ({ ...prev, rotation: undefined }))
        syncRotationDisplay(deg)
        return
      }

      commitRotation(deg)
    },
    [
      activeObject,
      activeObjects,
      commitRotation,
      getMixedMultiSelectPanelRotationDeg,
      isMultiSelect,
      shapeManagerRef,
      rotationEditStartValueRef,
      rotationRef,
      setFieldErrors,
      setRotationDisplay,
      syncRotationDisplay,
    ]
  )

  return { applyRotationToSelection, commitRotation, commitRotationFromText }
}
